import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Sheet (o "Drawer" do NDS — o nome diverge, o papel não).
 *
 * Usado em duas situações nesta aplicação: navegação off-canvas no mobile e
 * painel de detalhe da versão, aberto a partir do feed. Toda duração aponta para `duration-slow`;
 * o playbook registra que no app de referência este componente tinha
 * `duration-300/500` soltos no `cva()`, e essa é a correção.
 */

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { side?: 'left' | 'right'; rotuloFechar?: string }
>(({ className, children, side = 'right', rotuloFechar = 'Fechar', ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-bg-overlay duration-slow ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'cmp-scroll-area fixed z-50 flex h-full w-[min(26rem,calc(100vw-3rem))] flex-col gap-4 overflow-y-auto border-border bg-bg-surface p-6 shadow-lg duration-slow ease-standard',
        side === 'right'
          ? 'inset-y-0 right-0 border-l data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right'
          : 'inset-y-0 left-0 border-r data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-button p-2 text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle hover:text-on-surface">
        <X className="size-4" aria-hidden="true" />
        <span className="sr-only">{rotuloFechar}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContent.displayName = 'SheetContent';

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('pr-8 text-card-title', className)} {...props} />
));
SheetTitle.displayName = 'SheetTitle';

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-body text-on-surface-muted', className)} {...props} />
));
SheetDescription.displayName = 'SheetDescription';
